<script setup>
import { ref, onMounted, computed, watch  } from "vue";
import { useRoute, useRouter } from "vue-router";
import axios from 'axios';
//import { useThrottleFn } from "@vueuse/core"; // optional, tốt cho performance (install nếu muốn)

// state
const isOpen = ref(false);
const categories = ref([]);
const loading = ref(true);
const error = ref(null);
const selectedId = ref(null);

const icons = {
  all: "/svg/all.svg",
  new: "/svg/new.svg",
  popular: "/svg/popular.svg",
};

const route = useRoute();
const router = useRouter();

// Default icon using uploaded file (system will map local path to URL).
// Developer note: you uploaded /mnt/data/f8825059-4aad-4c3b-8a51-dc51623ba1b0.png
const defaultIcon = "/mnt/data/f8825059-4aad-4c3b-8a51-dc51623ba1b0.png";

// Toggle sidebar open/close
const toggleSidebar = () => (isOpen.value = !isOpen.value);

// Compute active if current route starts with /category/:id
const isActiveCategory = (cat) => {
  // If your routes use /category/:slug or /category/:id adjust accordingly
  return route.path === `/category/${cat.id}` || route.params.categoryId == cat.id;
};

const closeSidebar = () => (isOpen.value = false);

// Load categories from API
async function loadCategories() {
  loading.value = true;
  error.value = null;
  try {
    const res = await fetch("/api/categories", {
      headers: { Accept: "application/json" },
    });
    if (!res.ok) throw new Error("Failed to load categories");
    const data = await res.json();
    // normalize: ensure each category has id, name, icon(optional)
    categories.value = data.map((c) => ({
      id: c.id,
      name: c.name,
      icon: c.icon_url || c.icon || defaultIcon,
      meta: c,
    }));
  } catch (err) {
    console.error(err);
    error.value = err.message || "Load error";
  } finally {
    loading.value = false;
  }
}

// Load games for a category (emit to parent)
async function loadGamesForCategory(cat) {
  // set selected
  selectedId.value = cat.id;
	console.log('loadGamesForCategory: ', cat);
  // navigate to category route (so URL updates and active class handled)
  router.push({ path: `/category/${cat.id}` }).catch(()=>{});

  // fetch games list from API
  try {
    const resp = await fetch(`/api/games/cat/${cat.id}`, {
      headers: { Accept: "application/json" },
    });
    if (!resp.ok) throw new Error("Failed to load games");
    const dataGame = await resp.json();
    // emit event so parent (Home.vue) can render games
    // In <script setup>, to emit use defineEmits
	// console.log(dataGame);
    emit("category-selected", { category: cat, games: dataGame });
  } catch (err) {
    console.error(err);
    // still emit with empty list so UI can show message
    emit("category-selected", { category: cat, games: [], error: err.message });
  }
}

async function loadAllGames() {
  try {
    const response = await axios.get('/api/games/all');
	const games = response.data;
	emit("category-selected", { games });
  } catch (e) {
    console.error(e);
    const games = [];
	emit("category-selected", { games });
  }
}

async function loadNewGames() {
  try {
    const response = await axios.get('/api/games/new');
	const games = response.data;
	emit("category-selected", { games });
  } catch (e) {
    console.error(e);
    const games = [];
	emit("category-selected", { games });
  }
}

async function loadPopularGames() {
  try {
    const response = await axios.get('/api/games/popular');
	const games = response.data;
	emit("category-selected", { games });
  } catch (e) {
    console.error(e);
    const games = [];
	emit("category-selected", { games });
  }
}

// defineEmits after usage
const emit = defineEmits(["category-selected"]);

async function handleRouteChange(route) {
  const path = route.path;

  // /new
  if (path.startsWith("/new")) {
    await loadNewGames();
    return;
  }

  // /popular
  if (path.startsWith("/popular")) {
    await loadPopularGames();
    return;
  }

  // /category/:id
  const categoryId = route.params.id || route.params.categoryId;
  if (categoryId) {
    const cat = categories.value.find(c => c.id == categoryId);
    if (cat) {
      await loadGamesForCategory(cat);
    }
    return;
  }

  // default: /
  await loadAllGames();
}

onMounted(async () => {
  await loadCategories();
  await handleRouteChange(route);
});

// tự chạy lại khi URL đổi (click menu hoặc reload)
watch(
  () => route.path,
  () => {
    closeSidebar();
    handleRouteChange(route);
  }
);

// (optional) if you want throttle on toggling or searching
// const throttledToggle = useThrottleFn(toggleSidebar, 150);
</script>

<template>
  <aside :class="['sidebar', { open: isOpen }]">
    <div class="sidebar-toggle" @click="toggleSidebar" aria-label="Toggle menu">☰</div>

    <div class="sidebar-content" role="navigation" aria-label="Main categories">
      <div class="menu-block">
        <router-link class="menu-item" :class="{ active: route.path === '/' }" to="/" @click="closeSidebar">
		  <img class="menu-svg" :src="icons.all" alt="all" />
		  <span class="menu-text" v-if="isOpen">All games</span>
		</router-link>

		<div class="menu-item static" @click="() => { $router.push('/new'); closeSidebar(); }" :class="{ active: route.path.startsWith('/new') }">
		  <img class="menu-svg" :src="icons.new" alt="new" />
		  <span class="menu-text" v-if="isOpen">New <!--<span class="badge">183</span>--></span>
		</div>

		<router-link class="menu-item" to="/popular" :class="{ active: route.path.startsWith('/popular') }" @click="closeSidebar">
		  <img class="menu-svg" :src="icons.popular" alt="popular" />
		  <span class="menu-text" v-if="isOpen">Popular</span>
		</router-link>
      </div>

      <hr />

      <div class="menu-title" v-if="isOpen">All categories</div>

      <!-- loading / error -->
      <div v-if="loading" class="loading">Loading...</div>
      <div v-else-if="error" class="loading">{{ error }}</div>

      <!-- rendered categories -->
      <div v-else class="categories-list">
        <button
          v-for="cat in categories"
          :key="cat.id"
          class="menu-item category"
          :class="{ active: isActiveCategory(cat) }"
          @click="loadGamesForCategory(cat)"
        >
          <!-- icon (SVG or image) -->
          <img class="menu-svg" :src="cat.icon" :alt="cat.name" />
          <span class="menu-text" v-if="isOpen">{{ cat.name }}</span>
        </button>
      </div>
    </div>
  </aside>
</template>

<style scoped>
/* base */
.sidebar {
  width: 70px;
  background: #2b2b2b;
  height: calc(100vh - 60px);
  position: fixed;
  top: 60px;
  left: 0;
  transition: width 280ms cubic-bezier(.2,.9,.25,1), box-shadow 200ms;
  overflow: hidden;
  z-index: 20;
  box-shadow: rgba(0,0,0,0.12) 0 6px 18px;
}
/* open state: smooth width + subtle scale animation for inner content */
.sidebar.open {
  width: 260px;
  box-shadow: rgba(0,0,0,0.25) 0 12px 36px;
}

.sidebar-toggle {
  width: 45px;
  height: 45px;
  background: #3a3a3a;
  border-radius: 12px;
  cursor: pointer;
  margin: 10px auto;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 20px;
  user-select: none;
}

/* content fades and slides in */
.sidebar-content {
  padding: 10px;
  opacity: 0;
  transform: translateX(-8px);
  transition: opacity 260ms ease, transform 260ms ease;
}
.sidebar.open .sidebar-content {
  opacity: 1;
  transform: translateX(0);
}

/* menu */
.menu-block { padding-bottom: 8px; }
.menu-item {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 10px;
  border-radius: 8px;
  cursor: pointer;
  background: transparent;
  color: #ddd;
  border: none;
  width: 100%;
  text-align: left;
  transition: background 180ms, color 180ms, transform 180ms;
}
.menu-item .menu-svg {
  width: 36px;
  height: 36px;
  border-radius: 8px;
  object-fit: cover;
  background: rgba(255,255,255,0.03);
}
.menu-item .menu-text {
  font-weight: 600;
  white-space: nowrap;
}

/* category specific */
.menu-item.category { justify-content: flex-start; }
.menu-item:hover { background: rgba(255,255,255,0.06); transform: translateX(2px); }

/* active highlight */
.menu-item.active {
  background: #fff;
  color: #000;
  transform: none;
}

/* meta */
.menu-title { margin-top: 10px; font-weight: 600; color: #bbb; padding-left: 8px; }
.badge {
  background: #59ff9e;
  color: #000;
  padding: 2px 8px;
  border-radius: 10px;
  font-size: 12px;
  margin-left: 8px;
}

/* loading */
.loading { color: #aaa; padding: 8px 10px; }

.menu-svg {
  width: 36px;
  height: 36px;
  border-radius: 8px;
  object-fit: contain;
  background: rgba(255,255,255,0.02);
  transition: transform .18s ease, filter .18s ease, box-shadow .18s ease;
  filter: drop-shadow(0 0 0 rgba(0,0,0,0));
}

/* hover slightly pop */
.menu-item:hover .menu-svg {
  transform: translateX(4px) scale(1.02);
}

/* active: stronger neon glow */
.menu-item.active .menu-svg {
  transform: translateX(0) scale(1.06);
  filter: drop-shadow(0 6px 16px rgba(123,97,255,0.32)) drop-shadow(0 2px 6px rgba(0,245,255,0.12));
  box-shadow: 0 6px 20px rgba(123,97,255,0.12), inset 0 -2px 6px rgba(0,0,0,0.08);
}

/* make badge color match neon */
.menu-item .badge {
  background: linear-gradient(90deg, #00f5ff 0%, #7b61ff 100%);
  color: #051019;
  font-weight: 700;
}

/* responsive: mobile behaviour - overlay style */
@media (max-width: 768px) {
  .sidebar {
    left: 0;
    top: 56px;
    width: 0;
    height: calc(100vh - 56px);
  }
  .sidebar.open {
    width: 86%;
    max-width: 320px;
    position: fixed;
    left: 0;
    box-shadow: rgba(0,0,0,0.5) 0 24px 48px;
  }
  .sidebar-toggle {
    position: fixed;
    left: 14px;
    top: 66px;
    z-index: 60;
  }
  .menu-item .menu-svg { width: 44px; height: 44px; }
  .menu-item .menu-text { font-size: 16px; }
}
</style>
